import React from 'react';
import { ComplianceCheck } from '../../utils/types';
import IssueCard from './IssueCard';
import FixIndex from './FixIndex';
import { FIX_GROUP_LABEL, FixGroup, groupIssues } from './fixGroups';

interface FixListProps {
  checks: ComplianceCheck[];
  onOpenEvidence?: (ref: { section: string; filter?: string }) => void;
}

export default function FixList({ checks, onOpenEvidence }: FixListProps) {
  const issues = checks.filter(
    (c) => c.status === 'non_compliant' || c.status === 'warning' || c.status === 'error'
  );

  // An errored scan produces zero checks; that must NOT be congratulated —
  // only a scan where checks actually ran (and passed) earns the green state.
  if (checks.length === 0) {
    return (
      <section className="fix-list__empty">
        <h3 className="fix-list__empty-title">No checks ran</h3>
        <p className="fix-list__empty-text">
          The scan did not produce any compliance checks. See the scan errors above for details.
        </p>
      </section>
    );
  }

  if (issues.length === 0) {
    return (
      <section className="fix-list__empty">
        <h3 className="fix-list__empty-title">All checks passed</h3>
        <p className="fix-list__empty-text">No compliance issues were found on this site.</p>
      </section>
    );
  }

  // Fix these / Recommended / Confirm it applies to you (#1660). A Confirm
  // warning is not a finding, so it stays out of the header count (#1653) and
  // its cards sit last, in a collapsed group.
  const groups = groupIssues(issues);

  // Split the header count the same way ReportHero's Issues / Warnings chips do (#1652):
  // non_compliant + error are issues, warning is a warning. A zero part is omitted.
  const issueCount = groups.fix.length;
  const warningCount = groups.recommended.length;
  const countLabel = [
    issueCount > 0 ? `${issueCount} issue${issueCount === 1 ? '' : 's'}` : null,
    warningCount > 0 ? `${warningCount} warning${warningCount === 1 ? '' : 's'}` : null,
  ]
    .filter(Boolean)
    .join(', ');

  const renderCards = (group: FixGroup) => (
    <div className="fix-list__cards">
      {groups[group].map((check) => (
        <IssueCard key={check.id} check={check} group={group} onOpenEvidence={onOpenEvidence} />
      ))}
    </div>
  );

  return (
    <section className="fix-list">
      <div className="report-section-header">
        <span className="report-section-header__num">01</span>
        <h2 className="report-section-header__title">What to fix</h2>
        <span className="report-section-header__meta">
          {countLabel ? `${countLabel}, worst first` : 'No issues to fix'}
        </span>
      </div>

      <FixIndex groups={groups} />

      {(['fix', 'recommended'] as const).map(
        (group) =>
          groups[group].length > 0 && (
            <div key={group} className="fix-list__group">
              <p className="fix-list__group-heading">
                {FIX_GROUP_LABEL[group]} · {groups[group].length}
              </p>
              {renderCards(group)}
            </div>
          )
      )}

      {groups.confirm.length > 0 && (
        <details className="fix-list__advisory">
          <summary className="fix-list__advisory-summary">
            Confirm these apply to you · {groups.confirm.length}
            <span className="fix-list__advisory-note">no action needed if they don&apos;t</span>
          </summary>
          {renderCards('confirm')}
        </details>
      )}
    </section>
  );
}
